import React, { useState } from 'react';
import {
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  CurrencyPoundIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  HeartIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  StarIcon,
  CogIcon,
  ClockIcon,
  BanknotesIcon,
  IdentificationIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import DocumentManagement from './DocumentManagement';

const EmployeeDetailModal = ({ employee, onClose, onEdit }) => {
  const [activeTab, setActiveTab] = useState('personal');

  const tabs = [
    { id: 'personal', name: 'Personal', icon: UserIcon },
    { id: 'employment', name: 'Employment', icon: BriefcaseIcon },
    { id: 'contact', name: 'Contact', icon: PhoneIcon },
    { id: 'skills', name: 'Skills & Education', icon: AcademicCapIcon },
    { id: 'payroll', name: 'Payroll & Benefits', icon: CurrencyPoundIcon },
    { id: 'performance', name: 'Performance', icon: StarIcon },
    { id: 'health', name: 'Health & Safety', icon: HeartIcon },
    { id: 'documents', name: 'Documents', icon: DocumentTextIcon }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'terminated': return 'text-red-600 bg-red-100';
      case 'suspended': return 'text-yellow-600 bg-yellow-100';
      case 'on_leave': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getEmploymentTypeColor = (type) => {
    switch (type) {
      case 'full-time': return 'text-blue-600 bg-blue-100';
      case 'part-time': return 'text-purple-600 bg-purple-100';
      case 'contract': return 'text-orange-600 bg-orange-100';
      case 'temporary': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not specified';
    return new Date(date).toLocaleDateString('en-GB');
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  if (!employee) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-primary-600 rounded-full flex items-center justify-center">
                <span className="text-white text-xl font-semibold">
                  {employee?.first_name?.charAt(0) || 'E'}
                </span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {employee?.first_name} {employee?.last_name}
                </h2>
                <p className="text-gray-600">{employee?.job_title || 'No job title specified'}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(employee?.employment_status)}`}>
                    {employee?.employment_status?.replace('_', ' ').toUpperCase()}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEmploymentTypeColor(employee?.employment_type)}`}>
                    {employee?.employment_type?.replace('-', ' ').toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={onEdit}
                className="btn-secondary flex items-center space-x-2"
              >
                <PencilIcon className="w-4 h-4" />
                <span>Edit</span>
              </button>
              <button
                onClick={onClose}
                className="btn-secondary flex items-center space-x-2"
              >
                <XCircleIcon className="w-4 h-4" />
                <span>Close</span>
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex flex-wrap gap-2 md:gap-4 lg:gap-6 overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-3 border-b-2 font-medium text-xs sm:text-sm flex items-center space-x-1 sm:space-x-2 whitespace-nowrap flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="hidden sm:inline">{tab.name}</span>
                    <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {/* Personal Information */}
            {activeTab === 'personal' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <UserIcon className="w-5 h-5 mr-2" />
                    Basic Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Employee Number</label>
                      <p className="text-gray-900">{employee?.employee_number || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Title</label>
                      <p className="text-gray-900">{employee?.title || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Full Name</label>
                      <p className="text-gray-900">{employee?.first_name} {employee?.middle_name} {employee?.last_name}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Preferred Name</label>
                      <p className="text-gray-900">{employee?.preferred_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date of Birth</label>
                      <p className="text-gray-900">{formatDate(employee?.date_of_birth)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Gender</label>
                      <p className="text-gray-900">{employee?.gender || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Marital Status</label>
                      <p className="text-gray-900">{employee?.marital_status || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <IdentificationIcon className="w-5 h-5 mr-2" />
                    Legal Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">National Insurance Number</label>
                      <p className="text-gray-900">{employee?.national_insurance_number || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Passport Number</label>
                      <p className="text-gray-900">{employee?.passport_number || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Passport Expiry</label>
                      <p className="text-gray-900">
                        {employee?.passport_expiry ? new Date(employee.passport_expiry).toLocaleDateString() : 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Driving License</label>
                      <p className="text-gray-900">{employee?.driving_license_number || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Right to Work</label>
                      <div className="flex items-center">
                        {employee?.right_to_work ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.right_to_work ? 'text-green-600' : 'text-red-600'}>
                          {employee?.right_to_work ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Visa Type</label>
                      <p className="text-gray-900">{employee?.visa_type || 'Not applicable'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Employment Information */}
            {activeTab === 'employment' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BriefcaseIcon className="w-5 h-5 mr-2" />
                    Employment Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Job Title</label>
                      <p className="text-gray-900">{employee?.job_title || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Department</label>
                      <p className="text-gray-900">{employee?.department || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Division</label>
                      <p className="text-gray-900">{employee?.division || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Location</label>
                      <p className="text-gray-900">{employee?.location || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Work Location Type</label>
                      <p className="text-gray-900">{employee?.work_location_type || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Employment Category</label>
                      <p className="text-gray-900">{employee?.employment_category || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="w-5 h-5 mr-2" />
                    Employment Dates
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Hire Date</label>
                      <p className="text-gray-900">{formatDate(employee?.hire_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Probation Period</label>
                      <p className="text-gray-900">{employee?.probation_months || 6} months</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Probation End Date</label>
                      <p className="text-gray-900">
                        {formatDate(employee?.probation_end_date)}
                        {employee?.probation_end_date && (
                          <span className="text-xs text-gray-500 ml-2">(Auto-calculated)</span>
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Confirmation Date</label>
                      <p className="text-gray-900">{formatDate(employee?.confirmation_date)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Salary Review</label>
                      <p className="text-gray-900">{formatDate(employee?.last_salary_review)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Next Salary Review</label>
                      <p className="text-gray-900">{formatDate(employee?.next_salary_review)}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ClockIcon className="w-5 h-5 mr-2" />
                    Work Schedule
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Working Hours per Week</label>
                      <p className="text-gray-900">{employee?.working_hours_per_week || 'Not specified'} hours</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Working Days per Week</label>
                      <p className="text-gray-900">{employee?.working_days_per_week || 'Not specified'} days</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Start Time</label>
                      <p className="text-gray-900">{employee?.start_time || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">End Time</label>
                      <p className="text-gray-900">{employee?.end_time || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Break Duration</label>
                      <p className="text-gray-900">{employee?.break_duration_minutes || 'Not specified'} minutes</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Flexible Working</label>
                      <div className="flex items-center">
                        {employee?.flexible_working ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.flexible_working ? 'text-green-600' : 'text-red-600'}>
                          {employee?.flexible_working ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Information */}
            {activeTab === 'contact' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <PhoneIcon className="w-5 h-5 mr-2" />
                    Contact Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Personal Email</label>
                      <p className="text-gray-900">{employee?.personal_email || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Work Email</label>
                      <p className="text-gray-900">{employee?.work_email || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{employee?.phone || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Mobile</label>
                      <p className="text-gray-900">{employee?.mobile || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Work Phone</label>
                      <p className="text-gray-900">{employee?.work_phone || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Work Mobile</label>
                      <p className="text-gray-900">{employee?.work_mobile || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <MapPinIcon className="w-5 h-5 mr-2" />
                    Address Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Address Line 1</label>
                      <p className="text-gray-900">{employee?.address_line1 || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Address Line 2</label>
                      <p className="text-gray-900">{employee?.address_line2 || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">City</label>
                      <p className="text-gray-900">{employee?.city || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">County</label>
                      <p className="text-gray-900">{employee?.county || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Postcode</label>
                      <p className="text-gray-900">{employee?.postcode || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Country</label>
                      <p className="text-gray-900">{employee?.country || 'United Kingdom'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ExclamationTriangleIcon className="w-5 h-5 mr-2" />
                    Emergency Contact
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Name</label>
                      <p className="text-gray-900">{employee?.emergency_contact_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Phone</label>
                      <p className="text-gray-900">{employee?.emergency_contact_phone || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Mobile</label>
                      <p className="text-gray-900">{employee?.emergency_contact_mobile || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Email</label>
                      <p className="text-gray-900">{employee?.emergency_contact_email || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Relationship</label>
                      <p className="text-gray-900">{employee?.emergency_contact_relationship || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payroll & Benefits */}
            {activeTab === 'payroll' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CurrencyPoundIcon className="w-5 h-5 mr-2" />
                    Salary Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Salary Amount</label>
                      <p className="text-gray-900">{formatCurrency(employee?.salary_amount)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Salary Frequency</label>
                      <p className="text-gray-900">{employee?.salary_frequency || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Hourly Rate</label>
                      <p className="text-gray-900">{formatCurrency(employee?.hourly_rate)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Currency</label>
                      <p className="text-gray-900">{employee?.currency || 'GBP'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Bonus Eligible</label>
                      <div className="flex items-center">
                        {employee?.bonus_eligible ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.bonus_eligible ? 'text-green-600' : 'text-red-600'}>
                          {employee?.bonus_eligible ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Commission Eligible</label>
                      <div className="flex items-center">
                        {employee?.commission_eligible ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.commission_eligible ? 'text-green-600' : 'text-red-600'}>
                          {employee?.commission_eligible ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <BanknotesIcon className="w-5 h-5 mr-2" />
                    Bank Details
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Bank Name</label>
                      <p className="text-gray-900">{employee?.bank_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Holder Name</label>
                      <p className="text-gray-900">{employee?.account_holder_name || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Sort Code</label>
                      <p className="text-gray-900">{employee?.sort_code || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Account Number</label>
                      <p className="text-gray-900">{employee?.account_number ? '****' + employee.account_number.slice(-4) : 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ShieldCheckIcon className="w-5 h-5 mr-2" />
                    Tax & Pension
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Tax Code</label>
                      <p className="text-gray-900">{employee?.tax_code || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Pension Scheme</label>
                      <div className="flex items-center">
                        {employee?.pension_scheme ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.pension_scheme ? 'text-green-600' : 'text-red-600'}>
                          {employee?.pension_scheme ? 'Yes' : 'No'}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Pension Provider</label>
                      <p className="text-gray-900">{employee?.pension_provider || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Student Loan Plan</label>
                      <p className="text-gray-900">{employee?.student_loan_plan || 'Not applicable'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Skills & Education */}
            {activeTab === 'skills' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <AcademicCapIcon className="w-5 h-5 mr-2" />
                    Education & Qualifications
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Highest Qualification</label>
                      <p className="text-gray-900">{employee?.highest_qualification || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Institution</label>
                      <p className="text-gray-900">{employee?.qualification_institution || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Year</label>
                      <p className="text-gray-900">{employee?.qualification_year || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Professional Memberships</label>
                      <p className="text-gray-900">{employee?.professional_memberships || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Languages Spoken</label>
                      <p className="text-gray-900">{employee?.languages_spoken || 'Not specified'}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <CogIcon className="w-5 h-5 mr-2" />
                    Skills
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Technical Skills</label>
                      <p className="text-gray-900">{employee?.technical_skills || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Soft Skills</label>
                      <p className="text-gray-900">{employee?.soft_skills || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Hobbies & Interests</label>
                      <p className="text-gray-900">{employee?.hobbies_interests || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Volunteer Work</label>
                      <p className="text-gray-900">{employee?.volunteer_work || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Awards & Recognitions</label>
                      <p className="text-gray-900">{employee?.awards_recognitions || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Performance */}
            {activeTab === 'performance' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <StarIcon className="w-5 h-5 mr-2" />
                    Performance Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Performance Rating</label>
                      <p className="text-gray-900">{employee?.performance_rating || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Performance Review</label>
                      <p className="text-gray-900">{formatDate(employee?.last_performance_review)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Next Performance Review</label>
                      <p className="text-gray-900">{formatDate(employee?.next_performance_review)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Career Goals</label>
                      <p className="text-gray-900">{employee?.career_goals || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Training Needs</label>
                      <p className="text-gray-900">{employee?.training_needs || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Development Plan</label>
                      <p className="text-gray-900">{employee?.development_plan || 'Not specified'}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Health & Safety */}
            {activeTab === 'health' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <HeartIcon className="w-5 h-5 mr-2" />
                    Health Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Health Conditions</label>
                      <p className="text-gray-900">{employee?.health_conditions || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Allergies</label>
                      <p className="text-gray-900">{employee?.allergies || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Medical Emergency Contact</label>
                      <p className="text-gray-900">{employee?.medical_emergency_contact || 'Not specified'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last Health Assessment</label>
                      <p className="text-gray-900">{formatDate(employee?.last_health_assessment)}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Next Health Assessment</label>
                      <p className="text-gray-900">{formatDate(employee?.next_health_assessment)}</p>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                    <ShieldCheckIcon className="w-5 h-5 mr-2" />
                    Safety Training
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Health & Safety Training</label>
                      <div className="flex items-center">
                        {employee?.health_and_safety_training_completed ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-500 mr-2" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                        )}
                        <span className={employee?.health_and_safety_training_completed ? 'text-green-600' : 'text-red-600'}>
                          {employee?.health_and_safety_training_completed ? 'Completed' : 'Not Completed'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Documents */}
            {activeTab === 'documents' && (
              <div className="space-y-4">
                <DocumentManagement
                  employeeId={employee?.id}
                  employeeName={`${employee?.first_name || ''} ${employee?.last_name || ''}`.trim()}
                  canEdit={false}
                  canViewAll={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeDetailModal;
